
import { Language } from '@/context/LanguageContext';
import { getValidCountries, getValidCities } from './sitemapValidation';
import { generateSpecialtySitemapEntries } from './specialtyEntryGenerator';

interface SitemapEntry {
  url: string;
  lastmod: string;
  changefreq: string;
  priority: string;
  language?: Language;
}

// Generate comprehensive sitemap entries for all pages
export const generateSitemapEntries = async (baseUrl: string): Promise<SitemapEntry[]> => {
  const currentDate = new Date().toISOString().split('T')[0];
  const entries: SitemapEntry[] = [];
  const languages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];

  // Main pages for each language
  const mainPages = [
    { path: '/', priority: '1.0', changefreq: 'daily' },
    { path: '/find', priority: '0.9', changefreq: 'daily' },
    { path: '/how-it-works', priority: '0.8', changefreq: 'weekly' },
    { path: '/for-therapists', priority: '0.8', changefreq: 'weekly' },
    { path: '/about', priority: '0.7', changefreq: 'monthly' },
    { path: '/contact', priority: '0.7', changefreq: 'monthly' },
    { path: '/directory', priority: '0.9', changefreq: 'daily' },
    { path: '/specialties', priority: '0.8', changefreq: 'weekly' },
    { path: '/jose-luis-marin', priority: '0.8', changefreq: 'monthly' },
    { path: '/client-login', priority: '0.6', changefreq: 'monthly' },
    { path: '/client-signup', priority: '0.7', changefreq: 'monthly' },
    { path: '/therapist-login', priority: '0.6', changefreq: 'monthly' },
    { path: '/therapist-signup', priority: '0.7', changefreq: 'monthly' },
    { path: '/blog', priority: '0.8', changefreq: 'weekly' },
    { path: '/privacy', priority: '0.3', changefreq: 'yearly' },
    { path: '/terms', priority: '0.3', changefreq: 'yearly' },
    { path: '/cookie-policy', priority: '0.3', changefreq: 'yearly' },
    { path: '/sitemap', priority: '0.5', changefreq: 'weekly' }
  ];

  // Add main pages for each language
  languages.forEach(lang => {
    mainPages.forEach(page => {
      const url = lang === 'en' ? 
        `${baseUrl}${page.path}` : 
        `${baseUrl}/${lang}${page.path}`;
      
      entries.push({
        url,
        lastmod: currentDate,
        changefreq: page.changefreq,
        priority: page.priority,
        language: lang
      });
    });
  });

  // Country pages - enlazar a /psychologists/{country-slug} donde están las páginas reales
  const validCountries = getValidCountries();
  console.log(`🌍 Países validados para sitemap XML: ${validCountries.length}`);

  validCountries.forEach(country => {
    entries.push({
      url: `${baseUrl}/psychologists/${country.slug}`,
      lastmod: currentDate,
      changefreq: 'weekly',
      priority: '0.7',
      language: 'en'
    });
  });

  // City pages - comprehensive collection from validated cities
  const validCities = getValidCities();
  console.log(`🏙️ Ciudades validadas para sitemap XML: ${validCities.length}`);

  validCities.forEach(city => {
    entries.push({
      url: `${baseUrl}/city/${city.slug}`,
      lastmod: currentDate,
      changefreq: 'weekly',
      priority: '0.6',
      language: 'en'
    });
  });

  // 4. Add specialty pages for cities (ALL 32,853 cities × 26 specialties = 850,694 URLs)
  console.log('🎯 Generating specialty sitemap entries for ALL cities...');
  const specialtyEntries = await generateSpecialtySitemapEntries(baseUrl);
  entries.push(...specialtyEntries);
  
  console.log(`📄 Total sitemap entries generated: ${entries.length}`);

  return entries;
};
